import React, { useEffect } from 'react';
import { Plus, MessageSquare } from 'lucide-react';
import { useAgentChat } from '../../contexts/AgentChatContext';

const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Активен', color: 'var(--success-700)', bg: 'var(--success-50)' },
  completed: { label: 'Завершён', color: 'var(--gray-500)', bg: 'var(--gray-100)' },
  cancelled: { label: 'Отменён', color: 'var(--warning-700)', bg: 'var(--warning-50)' },
};

const AgentConversationList: React.FC = () => {
  const {
    conversations,
    conversationsLoading,
    loadConversations,
    loadConversation,
    startNewConversation,
    conversationId,
  } = useAgentChat();

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return (
    <div className="flex flex-col-dir flex-1 overflow-hidden">
      {/* New conversation button */}
      <div className="px-4-py-3">
        <button
          type="button"
          onClick={startNewConversation}
          className="btn-primary w-full justify-center text-sm flex items-center gap-2"
        >
          <Plus size={16} />
          Новый диалог
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '0 var(--space-4)' }}>
        {conversationsLoading ? (
          // Skeleton shimmer
          <div className="flex-col-g2">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="bg-gray-100 rounded-lg" style={{ height: 56, animation: 'shimmer 1.5s infinite' }}
              />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col-dir items-center justify-center gap-2" style={{ padding: 'var(--space-8) var(--space-4)' }}>
            <MessageSquare size={32} className="text-gray-300" />
            <span className="text-sm text-gray-500 text-center">
              Нет диалогов. Начните первый разговор с AI
            </span>
          </div>
        ) : (
          <div className="flex-col-g1">
            {conversations.map(conv => {
              const isActive = conv.id === conversationId;
              const status = statusLabels[conv.status] || statusLabels.active;

              return (
                <button
                  key={conv.id}
                  type="button"
                  onClick={() => loadConversation(conv.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-3)',
                    background: isActive ? 'var(--primary-50)' : 'transparent',
                    border: isActive ? '1px solid var(--primary-200)' : '1px solid transparent',
                    borderRadius: 'var(--radius-lg)',
                    cursor: 'pointer',
                    width: '100%',
                    fontFamily: 'inherit',
                    textAlign: 'left' as const,
                    transition: 'background 0.15s',
                    minHeight: 44,
                  }}
                  onMouseEnter={e => {
                    if (!isActive) e.currentTarget.style.background = 'var(--gray-50)';
                  }}
                  onMouseLeave={e => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div className="flex flex-col-dir" style={{ gap: 2 }}>
                    <span className="text-sm font-medium text-gray-800">
                      Диалог #{conv.id}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(conv.updated_at).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  <span className="rounded-full text-10 font-medium flex-shrink-0" style={{ padding: '2px var(--space-2)', color: status.color, background: status.bg }}>
                    {status.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentConversationList;
